import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import type { QrTagEventTypeEntry } from '../../constants/Constants'
import { createRfidTagEvent } from '../../modules/Rfid'
import { getErrorString } from '../../utils/errorUtils'
import RfidIdentifierInput from './RfidIdentifierInput'

type Coordinates = {
  latitude: number
  longitude: number
}

type Props = {
  group: IGroup
  tags: IRfidTag[]
  eventTypes: QrTagEventTypeEntry[]
  eventTypesLoading: boolean
  eventTypesError: string | null
  onReloadEventTypes: () => void
}

const RfidEventRecorder = ({
  group,
  tags,
  eventTypes,
  eventTypesLoading,
  eventTypesError,
  onReloadEventTypes,
}: Props) => {
  const [selectedTag, setSelectedTag] = useState<IRfidTag | null>(null)
  const [unregisteredIdentifier, setUnregisteredIdentifier] = useState<string | null>(null)
  const [eventTypeId, setEventTypeId] = useState('')
  const [notes, setNotes] = useState('')
  const [deviceId, setDeviceId] = useState('')
  const [location, setLocation] = useState<Coordinates | null>(null)
  const [locationError, setLocationError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const tagsByIdentifier = useMemo(
    () => new Map(tags.map((tag) => [tag.identifier, tag])),
    [tags],
  )

  useEffect(() => {
    const [firstEventType] = eventTypes
    if (!firstEventType) {
      setEventTypeId('')
      return
    }
    setEventTypeId((current) => (
      eventTypes.some((eventType) => String(eventType.id) === current)
        ? current
        : String(firstEventType.id)
    ))
  }, [eventTypes])

  useEffect(() => {
    setSelectedTag(null)
    setUnregisteredIdentifier(null)
    setNotes('')
    setDeviceId('')
    setError(null)
    setMessage(null)
  }, [group.id])

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocation(null)
      setLocationError('このブラウザでは位置情報を取得できません。位置情報なしで記録できます。')
      return
    }

    setLocationError(null)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        })
      },
      () => {
        setLocation(null)
        setLocationError('位置情報を取得できませんでした。位置情報なしで記録できます。')
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
      },
    )
  }, [])

  useEffect(() => {
    requestLocation()
  }, [requestLocation])

  const handleRead = (identifier: string) => {
    const tag = tagsByIdentifier.get(identifier)
    setError(null)
    setMessage(null)
    if (!tag) {
      setSelectedTag(null)
      setUnregisteredIdentifier(identifier)
      return
    }

    setUnregisteredIdentifier(null)
    setSelectedTag(tag)
  }

  const resetSelection = () => {
    setSelectedTag(null)
    setUnregisteredIdentifier(null)
    setNotes('')
    setError(null)
  }

  const handleSubmit = async () => {
    const parsedEventTypeId = Number(eventTypeId)
    if (!selectedTag || !Number.isFinite(parsedEventTypeId)) {
      setError('RFIDタグとイベント種別を確認してください。')
      return
    }

    setIsSubmitting(true)
    setError(null)
    setMessage(null)
    try {
      await createRfidTagEvent(group.id, {
        rfid_tag_id: selectedTag.id,
        event_type_id: parsedEventTypeId,
        occurred_at: new Date().toISOString(),
        notes: notes.trim() || undefined,
        latitude: location?.latitude,
        longitude: location?.longitude,
        device_id: deviceId ? Number(deviceId) as DeviceId : undefined,
      })
      setMessage(`${selectedTag.name}のイベントを記録しました。`)
      resetSelection()
    } catch (submitError) {
      setError(getErrorString(submitError))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className="rfid-panel" data-testid="rfid-event-recorder">
      <h3>RFIDイベント記録</h3>
      <p className="rfid-panel__description">
        RFIDを1件読み取り、グループのイベント種別を選んで記録します。
      </p>

      <RfidIdentifierInput
        id="rfid-event-identifier"
        onRead={handleRead}
        disabled={Boolean(selectedTag)}
      />

      {unregisteredIdentifier && (
        <div className="rfid-message rfid-message--error">
          未登録のRFIDです:
          {' '}
          <span className="rfid-code">{unregisteredIdentifier}</span>
        </div>
      )}
      {message && <div className="rfid-message rfid-message--success">{message}</div>}
      {error && <div className="rfid-message rfid-message--error">{error}</div>}

      {selectedTag && (
        <div className="rfid-event-form">
          <div className="rfid-selected-tag">
            <strong>{selectedTag.name}</strong>
            <span className="rfid-code">{selectedTag.identifier}</span>
          </div>
          <div className="rfid-field">
            <label htmlFor="rfid-event-type">イベント種別*</label>
            <select
              id="rfid-event-type"
              value={eventTypeId}
              disabled={eventTypesLoading || eventTypes.length === 0}
              onChange={(event) => setEventTypeId(event.target.value)}
            >
              {eventTypes.map((eventType) => (
                <option key={eventType.id} value={eventType.id}>{eventType.name}</option>
              ))}
            </select>
            {eventTypesLoading && <span className="rfid-help-text">読み込み中...</span>}
            {!eventTypesLoading && eventTypes.length === 0 && !eventTypesError && (
              <span className="rfid-message rfid-message--error">イベント種別が設定されていません。</span>
            )}
            {eventTypesError && (
              <span className="rfid-message rfid-message--error">
                {eventTypesError}
                {' '}
                <button type="button" className="btn btn-link" onClick={onReloadEventTypes}>
                  再読み込み
                </button>
              </span>
            )}
          </div>
          <div className="rfid-field">
            <label htmlFor="rfid-event-device">読み取り元Device</label>
            <select
              id="rfid-event-device"
              value={deviceId}
              onChange={(event) => setDeviceId(event.target.value)}
            >
              <option value="">指定しない</option>
              {(group.devices ?? []).map((device) => (
                <option key={device.id as number} value={device.id as number}>{device.name}</option>
              ))}
            </select>
          </div>
          <div className="rfid-field">
            <label htmlFor="rfid-event-notes">メモ</label>
            <textarea
              id="rfid-event-notes"
              value={notes}
              rows={3}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>
          <div className="rfid-location">
            {location ? (
              <span>
                位置情報:
                {' '}
                {location.latitude.toFixed(6)}
                ,
                {' '}
                {location.longitude.toFixed(6)}
              </span>
            ) : <span>{locationError || '位置情報を取得しています...'}</span>}
            <button type="button" className="btn btn-link" onClick={requestLocation}>
              位置情報を再取得
            </button>
          </div>
          <div className="rfid-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={isSubmitting || eventTypesLoading || !eventTypeId}
              onClick={handleSubmit}
            >
              {isSubmitting ? '記録中...' : 'イベントを記録する'}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={isSubmitting}
              onClick={resetSelection}
            >
              読み直す
            </button>
          </div>
        </div>
      )}
    </section>
  )
}

export default RfidEventRecorder
